import { isoAndUnixZodSchema } from "@@/shared/date-time/iso-and-unix";
import { priceZodSchema } from "@@/shared/prices/price";
import { z } from "zod";
import { payRequestPaymentEventZodSchema } from "../../events/_pay-request-payment.event";
import { payRequestReferencesZodSchema } from "../../events/_pay-request-references";
import { payRequestIdZodSchema } from "../../pay-request-id";
import { payRequestItemZodSchema } from "../../pay-request-item";
import { payRequestRecipientZodSchema } from "../../pay-request-recipient";

const payRequestProjectionZodObject = z.object({
    _id: payRequestIdZodSchema,
    _type: z.literal("PAY_REQUEST_PROJECTION"),
    _createdAt: isoAndUnixZodSchema,
    _version: z.number(),
    /** The current status of the pay request */
    status: z.enum(["PENDING", "CANCELLED", "EXPIRED", "FULFILLED"]),
    /** The current payment status of the pay request */
    paymentStatus: z.enum([
        "PENDING",
        "NOT_INITIATED",
        "FULFILLED",
        "FAILED",
        "CANCELLED",
        "EXPIRED",
    ]),
    /**
     * Contact info for payers of the pay request
     * a notification will (have) be(en) sent to these contacts
     */
    recipients: z.array(payRequestRecipientZodSchema).min(1),
    /** Items to be paid using the pay request */
    items: z.array(payRequestItemZodSchema).min(1),
    /** Pay request amount to be paid */
    price: priceZodSchema,
    /**
     * When someone initiates an attempt to pay the pay request,
     * payment events are created to follow the payment process(es)
     * E.g. the first attempt could fail, then the payer could try again
     * and more payment events would be created
     */
    paymentEvents: z.array(payRequestPaymentEventZodSchema),
    /**
     * An object with references to the order, order group, order events and provider that the
     * pay request is a payment for
     * */
    paymentFor: payRequestReferencesZodSchema,
});

type PayRequestProjection = z.infer<typeof payRequestProjectionZodObject>;
const payRequestProjectionZodSchema: z.ZodSchema<PayRequestProjection> =
    payRequestProjectionZodObject;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isPayRequestProjection = (item: any): item is PayRequestProjection => {
    return item._type === "PAY_REQUEST_PROJECTION";
};

export {
    isPayRequestProjection,
    payRequestProjectionZodObject,
    payRequestProjectionZodSchema,
};
export type { PayRequestProjection };
